import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Twitter, LinkedIn, GitHub, Instagram } from '@mui/icons-material';

/**
 * Cinematic / editorial footer. Hairline columns, mono labels,
 * serif italic brand mark. Theme-aware via the design tokens
 * defined in index.css (works in light, dark, and sepia).
 */
const Footer: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const footerLinks: Record<string, any> = {
    en: {
      product: 'Product', features: 'Features', pricing: 'Pricing', demos: 'Demos',
      resources: 'Resources', blog: 'Blog', faq: 'FAQ',
      company: 'Company', contact: 'Contact', privacy: 'Privacy Policy', terms: 'Terms of Service', kvkk: 'GDPR',
      followUs: 'Follow', tagline: 'AI-powered script analysis for filmmakers and screenwriters.',
    },
    tr: {
      product: 'Ürün', features: 'Özellikler', pricing: 'Fiyatlandırma', demos: 'Demolar',
      resources: 'Kaynaklar', blog: 'Blog', faq: 'SSS',
      company: 'Şirket', contact: 'İletişim', privacy: 'Gizlilik', terms: 'Kullanım Şartları', kvkk: 'KVKK',
      followUs: 'Takip Et', tagline: 'Senaryolar için yapay zekâ destekli analiz platformu.',
    },
    fr: {
      product: 'Produit', features: 'Fonctionnalités', pricing: 'Tarification', demos: 'Démos',
      resources: 'Ressources', blog: 'Blog', faq: 'FAQ',
      company: 'Entreprise', contact: 'Contact', privacy: 'Confidentialité', terms: 'Conditions', kvkk: 'RGPD',
      followUs: 'Suivre', tagline: "Plateforme d'analyse de scénarios alimentée par l'IA.",
    },
    es: {
      product: 'Producto', features: 'Características', pricing: 'Precios', demos: 'Demos',
      resources: 'Recursos', blog: 'Blog', faq: 'FAQ',
      company: 'Empresa', contact: 'Contacto', privacy: 'Privacidad', terms: 'Términos', kvkk: 'RGPD',
      followUs: 'Seguir', tagline: 'Análisis de guiones con IA para cineastas y guionistas.',
    },
    ar: {
      product: 'المنتج', features: 'الميزات', pricing: 'الأسعار', demos: 'العروض التوضيحية',
      resources: 'الموارد', blog: 'المدونة', faq: 'الأسئلة الشائعة',
      company: 'الشركة', contact: 'اتصل بنا', privacy: 'الخصوصية', terms: 'الشروط', kvkk: 'حماية البيانات',
      followUs: 'تابعنا', tagline: 'منصة تحليل السيناريو بالذكاء الاصطناعي.',
    },
    de: {
      product: 'Produkt', features: 'Funktionen', pricing: 'Preise', demos: 'Demos',
      resources: 'Ressourcen', blog: 'Blog', faq: 'FAQ',
      company: 'Unternehmen', contact: 'Kontakt', privacy: 'Datenschutz', terms: 'Nutzungsbedingungen', kvkk: 'DSGVO',
      followUs: 'Folgen', tagline: 'KI-gestützte Drehbuchanalyse für Filmemacher.',
    },
    it: {
      product: 'Prodotto', features: 'Funzionalità', pricing: 'Prezzi', demos: 'Demo',
      resources: 'Risorse', blog: 'Blog', faq: 'FAQ',
      company: 'Azienda', contact: 'Contatto', privacy: 'Privacy', terms: 'Termini', kvkk: 'GDPR',
      followUs: 'Segui', tagline: 'Analisi sceneggiature con IA per registi e sceneggiatori.',
    },
    ru: {
      product: 'Продукт', features: 'Функции', pricing: 'Цены', demos: 'Демо',
      resources: 'Ресурсы', blog: 'Блог', faq: 'FAQ',
      company: 'Компания', contact: 'Контакты', privacy: 'Конфиденциальность', terms: 'Условия', kvkk: 'GDPR',
      followUs: 'Подписаться', tagline: 'ИИ-анализ сценариев для кинематографистов.',
    },
  };

  const links = footerLinks[language] || footerLinks.en;

  const socialLinks = {
    twitter: 'https://twitter.com/mrmirrorai',
    linkedin: 'https://linkedin.com/company/mrmirrorai',
    github: 'https://github.com/mrmirrorai',
    instagram: 'https://instagram.com/mrmirrorai',
  };

  const colHeader = 'eyebrow-mute mb-4 block';
  const colLink =
    'block py-1.5 font-mono text-[11px] tracking-[0.18em] uppercase text-ink-dim hover:text-accent transition-colors';

  return (
    <footer className="border-t border-hairline bg-background relative">
      {/* Top mono caption rule */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12">
        <div className="section-rule pb-10">
          <span>FIN</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8 mb-12">
          {/* Brand block */}
          <div className="col-span-2 md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/mr-mirror-logo.png"
                alt=""
                className="h-8 w-8 object-contain"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
              <span className="font-serif italic text-2xl leading-none text-ink">
                Mr.Mirror
                <sup className="font-mono not-italic text-[10px] text-accent ml-0.5 tracking-wider">®</sup>
              </span>
            </div>
            <p className="font-prose text-[15px] leading-relaxed text-ink-dim max-w-md">
              {links.tagline}
            </p>
          </div>

          {/* Product */}
          <div className="md:col-span-2">
            <span className={colHeader}>/ {links.product}</span>
            <button onClick={() => navigate('/features')} className={colLink}>{links.features}</button>
            <button onClick={() => navigate('/pricing')} className={colLink}>{links.pricing}</button>
            <button onClick={() => navigate('/demos')} className={colLink}>{links.demos}</button>
          </div>

          {/* Resources */}
          <div className="md:col-span-2">
            <span className={colHeader}>/ {links.resources}</span>
            <button onClick={() => navigate('/blog')} className={colLink}>{links.blog}</button>
            <button onClick={() => navigate('/faq')} className={colLink}>{links.faq}</button>
          </div>

          {/* Company / Legal */}
          <div className="md:col-span-3">
            <span className={colHeader}>/ {links.company}</span>
            <button onClick={() => navigate('/contact')} className={colLink}>{links.contact}</button>
            <button onClick={() => navigate('/privacy')} className={colLink}>{links.privacy}</button>
            <button onClick={() => navigate('/terms')} className={colLink}>{links.terms}</button>
            <button onClick={() => navigate('/kvkk')} className={colLink}>{links.kvkk}</button>
          </div>
        </div>

        {/* Social + Copyright row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-8 border-t border-hairline">
          <div className="flex items-center gap-2">
            <span className="eyebrow-mute mr-3">{links.followUs}</span>
            <a
              href={socialLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center border border-hairline text-ink-dim hover:text-accent hover:border-accent transition-colors"
              aria-label="Twitter"
            >
              <Twitter sx={{ fontSize: 16 }} />
            </a>
            <a
              href={socialLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center border border-hairline text-ink-dim hover:text-accent hover:border-accent transition-colors"
              aria-label="LinkedIn"
            >
              <LinkedIn sx={{ fontSize: 16 }} />
            </a>
            <a
              href={socialLinks.github}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center border border-hairline text-ink-dim hover:text-accent hover:border-accent transition-colors"
              aria-label="GitHub"
            >
              <GitHub sx={{ fontSize: 16 }} />
            </a>
            <a
              href={socialLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center border border-hairline text-ink-dim hover:text-accent hover:border-accent transition-colors"
              aria-label="Instagram"
            >
              <Instagram sx={{ fontSize: 16 }} />
            </a>
          </div>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink-mute">
            © {currentYear} · Mr.Mirror
            <sup className="ml-0.5 text-accent">®</sup>
            {language === 'tr' ? ' · Tüm hakları saklıdır' : ' · All rights reserved'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
